/**
 * ColorMode — light / dark / system, for the whole app.
 *
 * Three states, not two. "System" is the default and the one most people want:
 * it follows the OS and switches live when the OS does (via the media-query
 * listener), so a machine set to flip at sunset flips XEO at sunset. An explicit
 * Light or Dark choice overrides that until the user picks System again.
 *
 * The resolved mode is stamped on <html data-theme> as well as handed to MUI,
 * because the page GROUND is painted in index.css — this app renders no
 * <CssBaseline>, and the document background must be right before React mounts
 * or the first frame flashes the wrong colour.
 *
 *   const { resolved, toggle } = useColorMode();
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { makeTheme } from '../theme';

const KEY = 'xeoColorMode';
// Stored values: 'light' | 'dark' | 'system'. 'system' is only ever the INITIAL
// state — the first visit follows the OS — and the toggle then writes an explicit
// choice. Cycling through three states was the bug: System-resolving-dark and
// Dark look identical, so one of the three clicks did nothing visible.
const MODES = ['system', 'light', 'dark'];

const ColorModeContext = createContext({ mode: 'system', resolved: 'light', setMode: () => {}, toggle: () => {} });
export const useColorMode = () => useContext(ColorModeContext);

const readStored = () => {
    try {
        const v = localStorage.getItem(KEY);
        return MODES.includes(v) ? v : 'system';
    } catch (_) { return 'system'; }
};

const systemPrefersDark = () => {
    try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch (_) { return false; }
};

export function ColorModeProvider({ children }) {
    const [mode, setModeState] = useState(readStored);
    const [systemDark, setSystemDark] = useState(systemPrefersDark);

    // Follow the OS while in system mode. The listener stays attached in every
    // mode, so switching back to System is instantly correct.
    useEffect(() => {
        let mq;
        try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch (_) { return undefined; }
        const onChange = (e) => setSystemDark(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    const resolved = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

    // Stamp the attribute BEFORE the children render, too (audit 2026-11-01).
    // Chart.js colours are read from the tokens with cssVar() during render, and
    // the effect below runs only after the tree has rendered — so the Response
    // Time chart painted the previous mode's gridlines after every switch.
    // Idempotent; the effect stays for colorScheme.
    if (typeof document !== 'undefined' && document.documentElement.dataset.theme !== resolved) {
        document.documentElement.dataset.theme = resolved;
    }

    useEffect(() => {
        document.documentElement.dataset.theme = resolved;
        // Tells the browser to paint form controls, scrollbars and the overscroll
        // area for this mode — the parts CSS can't reach.
        document.documentElement.style.colorScheme = resolved;
    }, [resolved]);

    const setMode = useCallback((next) => {
        const m = MODES.includes(next) ? next : 'system';
        setModeState(m);
        try { localStorage.setItem(KEY, m); } catch (_) { /* private window — session only */ }
    }, []);

    // Flip to the opposite of what is ON SCREEN, whatever produced it. From
    // system-dark, one click gives light — never another dark.
    const toggle = useCallback(() => {
        setMode(resolved === 'dark' ? 'light' : 'dark');
    }, [resolved, setMode]);

    const theme = useMemo(() => makeTheme(resolved), [resolved]);
    const value = useMemo(() => ({ mode, resolved, setMode, toggle }), [mode, resolved, setMode, toggle]);

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default ColorModeProvider;
